import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAppData } from '../data';
import { useWalkthrough } from '../walkthrough';
import { RatePrompt } from './RatePrompt';

type RatePromptContextValue = {
  show: () => void;
};

const RatePromptContext = createContext<RatePromptContextValue | null>(null);

export function useRatePrompt(): RatePromptContextValue {
  const ctx = useContext(RatePromptContext);
  if (!ctx) {
    throw new Error('useRatePrompt must be used inside <RatePromptProvider>');
  }
  return ctx;
}

const FIVE_TXNS = 5;
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Decide whether the auto-trigger conditions are met.
 *   - User has rated already           -> never
 *   - User said "stop asking"          -> never
 *   - Onboarding or walkthrough active -> hold off (don't compete)
 *   - Snoozed within the last 3 days   -> hold off
 *   - Fewer than 5 manual transactions -> not yet
 */
function shouldAutoShow(
  data: ReturnType<typeof useAppData>,
  walkthroughActive: boolean,
  now: number,
): boolean {
  if (data.rated) return false;
  if (data.rateNever) return false;
  if (!data.profile.onboardingComplete) return false;
  if (walkthroughActive) return false;
  if (data.rateSnoozed) {
    const ts = Date.parse(data.rateSnoozed);
    if (Number.isFinite(ts) && now - ts < SNOOZE_MS) return false;
  }
  const manualCount = data.transactions.filter(
    (t) => t.source === 'manual',
  ).length;
  return manualCount >= FIVE_TXNS;
}

/**
 * Owns the rate prompt's open state. Auto-fires when the conditions
 * line up; the `show()` callback exposes the same prompt manually
 * (Settings → Help → Rate Margin). Per-mount guard prevents the auto
 * branch from re-firing after the user dismisses without snoozing
 * (e.g. taps outside the BottomSheet).
 */
export function RatePromptProvider({ children }: { children: ReactNode }) {
  const data = useAppData();
  const walkthrough = useWalkthrough();
  const [open, setOpen] = useState(false);
  const autoFired = useRef(false);

  const show = useCallback(() => setOpen(true), []);

  useEffect(() => {
    if (autoFired.current) return;
    if (shouldAutoShow(data, walkthrough.active, Date.now())) {
      autoFired.current = true;
      setOpen(true);
    }
  }, [
    data,
    walkthrough.active,
  ]);

  const value = useMemo<RatePromptContextValue>(() => ({ show }), [show]);

  return (
    <RatePromptContext.Provider value={value}>
      {children}
      <RatePrompt open={open} onClose={() => setOpen(false)} />
    </RatePromptContext.Provider>
  );
}
